import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { localDateSchema, timestampSchema, uuidSchema } from "../common/primitives.js";
import { agentAlarmSeveritySchema, agentArchSchema, agentCommandStatusSchema, agentCommandTypeSchema, agentOsSchema, agentStatusSchema, agentUpdateChannelSchema, } from "./enums.js";
import { agentAdapterHealthSchema, agentConfigOverrideSchema } from "./protocol.js";
/**
 * Ajan YÖNETİMİ (kullanıcı uçları; özellik bayrağı `STATION_AGENT`).
 *
 * İzinler: okuma `agents.read`, kayıt kodu / iptal `agents.manage`, komut gönderme `agents.command`.
 * KAPSAM denetimi istasyon düzeyindedir: yalnızca kendi istasyonlarına atanmış müdür o istasyonun ajanını görür.
 * KVKK: kayıt kodunun düz metni YALNIZCA üretim yanıtında döner; açık anahtar ve token hiçbir yanıtta / günlükte yer almaz.
 */
// ── Ajan kartı ───────────────────────────────────────────────────────────────
export const agentSummarySchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema,
    stationCode: z.string(),
    stationName: z.string(),
    status: agentStatusSchema,
    hostname: z.string(),
    os: agentOsSchema,
    arch: agentArchSchema,
    version: z.string(),
    updateChannel: agentUpdateChannelSchema,
    online: z.boolean(),
    lastSeenAt: timestampSchema.nullable(),
    clockSkewMs: z.number().int().nullable(),
    queueDepth: z.number().int().nullable(),
    oldestPendingAt: timestampSchema.nullable(),
    lastAckedSeq: z.string().describe("bigint, dizge"),
    lastSyncAt: timestampSchema.nullable(),
    pendingCommands: z.number().int().nonnegative(),
    /**
     * SON heartbeat'ten TÜRETİLEN sağlık özeti (liste "3 / 3 çalışıyor" diyebilsin, her satır için ayrıntı çekmesin).
     * Ajan hiç heartbeat atmadıysa sayılar 0, ölçüler `null`.
     */
    adapterTotal: z.number().int().nonnegative(),
    adapterOk: z.number().int().nonnegative(),
    failingAdapters: z.array(z.string()).describe("Durumu OK olmayan adaptörlerin kimlikleri"),
    deadLetterCount: z.number().int().nonnegative().nullable(),
    uptimeSeconds: z.number().int().nonnegative().nullable(),
    diskFreeBytes: z.number().nonnegative().nullable(),
    diskTotalBytes: z.number().nonnegative().nullable(),
    memoryRssBytes: z.number().nonnegative().nullable(),
    enrolledAt: timestampSchema,
    revokedAt: timestampSchema.nullable(),
    revokeReason: z.string().nullable(),
});
export const agentDeviceStateSchema = z.object({
    deviceKind: z.enum(["NOZZLE", "DEVICE"]),
    deviceRef: z.string(),
    /** Okunaklı ad: tabancada "1. ada · P1 · T1 — Motorin"; tanınmayan cihazda `null` (arayüz `deviceRef`'e düşer). */
    label: z.string().nullable(),
    adapterId: z.string().nullable(),
    status: z.string(),
    detail: z.string().nullable(),
    deviceTime: timestampSchema.nullable(),
    receivedAt: timestampSchema,
});
export const agentDetailSchema = agentSummarySchema.extend({
    adapters: z.array(agentAdapterHealthSchema),
    deviceStates: z.array(agentDeviceStateSchema),
    config: agentConfigOverrideSchema,
    lastHeartbeatAt: timestampSchema.nullable(),
    /** Son heartbeat gövdesinin ham hâli (panelde "ayrıntı" bölümü); kişisel veri taşımaz. */
    lastHeartbeat: z.record(z.string(), z.unknown()).nullable(),
    versionNo: z.number().int().min(1),
});
export const agentPageSchema = pageOf(agentSummarySchema);
export const listAgentsQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    stationId: uuidSchema.optional(),
    status: agentStatusSchema.optional(),
    online: z.coerce.boolean().optional(),
});
export const agentParamsSchema = z.object({ id: uuidSchema });
export const agentCommandParamsSchema = z.object({ id: uuidSchema, commandId: uuidSchema });
export const revokeAgentSchema = z.object({
    reason: z.string().trim().min(3).max(500).describe("Neden iptal edildiği (denetim izine yazılır)"),
});
// ── Heartbeat geçmişi ────────────────────────────────────────────────────────
export const agentHeartbeatRecordSchema = z.object({
    id: uuidSchema,
    receivedAt: timestampSchema,
    sentAt: timestampSchema,
    version: z.string(),
    queueDepth: z.number().int(),
    deadLetterCount: z.number().int(),
    clockSkewMs: z.number().int(),
    uptimeSeconds: z.string().nullable().describe("bigint, dizge"),
    summary: z.record(z.string(), z.unknown()),
});
export const agentHeartbeatPageSchema = pageOf(agentHeartbeatRecordSchema);
export const listAgentHeartbeatsQuerySchema = pageQuerySchema;
// ── Komutlar (yönetim görünümü) ─────────────────────────────────────────────
export const agentCommandRecordSchema = z.object({
    id: uuidSchema,
    agentId: uuidSchema,
    stationId: uuidSchema,
    type: agentCommandTypeSchema,
    status: agentCommandStatusSchema,
    attempt: z.number().int(),
    payload: z.record(z.string(), z.unknown()),
    createdBy: uuidSchema.nullable().describe("null = sistem (olay tüketicisi)"),
    sourceEventId: uuidSchema.nullable(),
    expiresAt: timestampSchema,
    deliveredAt: timestampSchema.nullable(),
    ackedAt: timestampSchema.nullable(),
    finishedAt: timestampSchema.nullable(),
    resultDetail: z.string().nullable(),
    resultOutput: z.record(z.string(), z.unknown()).nullable(),
    createdAt: timestampSchema,
    version: z.number().int().min(1),
});
export const agentCommandPageSchema = pageOf(agentCommandRecordSchema);
export const listAgentCommandsQuerySchema = pageQuerySchema.extend({
    status: agentCommandStatusSchema.optional(),
    type: agentCommandTypeSchema.optional(),
});
/**
 * Elle komut. `UPDATE_PRICES` yükü SUNUCUDA doldurulur ("fiyatları yeniden gönder"): istemciden gelen
 * `payload` yok sayılır, o anki YÜRÜRLÜKTEKİ istasyon fiyatları yazılır.
 */
export const createAgentCommandSchema = z.object({
    type: agentCommandTypeSchema,
    payload: z.record(z.string(), z.unknown()).default({}),
    ttlSeconds: z.number().int().min(30).max(86_400).default(900),
});
// ── Alarmlar ─────────────────────────────────────────────────────────────────
export const agentAlarmRecordSchema = z.object({
    id: uuidSchema,
    agentId: uuidSchema,
    stationId: uuidSchema,
    adapterId: z.string(),
    deviceRef: z.string(),
    code: z.string(),
    severity: agentAlarmSeveritySchema,
    message: z.string(),
    deviceTime: timestampSchema.nullable(),
    raisedAt: timestampSchema,
});
export const agentAlarmPageSchema = pageOf(agentAlarmRecordSchema);
export const listAgentAlarmsQuerySchema = pageQuerySchema.extend({
    severity: agentAlarmSeveritySchema.optional(),
    from: localDateSchema.optional(),
});
// ── Günlük yüklemeleri ───────────────────────────────────────────────────────
export const agentLogUploadRecordSchema = z.object({
    id: uuidSchema,
    agentId: uuidSchema,
    commandId: uuidSchema.nullable(),
    sizeBytes: z.string().describe("bigint, dizge"),
    contentEncoding: z.string(),
    uploadedAt: timestampSchema,
    /** Kısa ömürlü ön imzalı indirme adresi. */
    downloadUrl: z.string(),
    downloadExpiresAt: timestampSchema,
});
export const agentLogUploadPageSchema = pageOf(agentLogUploadRecordSchema);
export const listAgentLogUploadsQuerySchema = pageQuerySchema;
// ── Kayıt kodları ────────────────────────────────────────────────────────────
export const agentEnrollmentCodeSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema,
    stationCode: z.string(),
    stationName: z.string(),
    expiresAt: timestampSchema,
    usedAt: timestampSchema.nullable(),
    usedByAgentId: uuidSchema.nullable(),
    revokedAt: timestampSchema.nullable(),
    note: z.string().nullable(),
    createdBy: uuidSchema.nullable(),
    createdAt: timestampSchema,
    /** Türetilmiş durum: kullanılmış / iptal / süresi dolmuş / geçerli. */
    state: z.enum(["ACTIVE", "USED", "REVOKED", "EXPIRED"]),
});
export const agentEnrollmentCodeListSchema = z.object({ items: z.array(agentEnrollmentCodeSchema) });
export const listAgentEnrollmentCodesQuerySchema = z.object({
    companyId: uuidSchema,
    stationId: uuidSchema.optional(),
    activeOnly: z.coerce.boolean().optional(),
});
export const createAgentEnrollmentCodeSchema = z.object({
    stationId: uuidSchema,
    note: z.string().trim().min(1).max(200).optional(),
});
/** Düz kod YALNIZCA burada döner; sunucuda yalnızca SHA-256 özeti saklanır. */
export const createdAgentEnrollmentCodeSchema = z.object({
    id: uuidSchema,
    code: z.string().describe("Düz kod — bir kez gösterilir, bir daha okunamaz"),
    expiresAt: timestampSchema,
    stationId: uuidSchema,
});
// ── İstasyon panosu özeti ────────────────────────────────────────────────────
export const agentStationSummarySchema = z.object({
    stationId: uuidSchema,
    stationCode: z.string(),
    stationName: z.string(),
    activeAgents: z.number().int().nonnegative(),
    onlineAgents: z.number().int().nonnegative(),
    offlineAgents: z.number().int().nonnegative(),
    /** Bekleyen en eski olayın anı (kuyruk yaşı); hiçbiri beklemiyorsa null. */
    oldestPendingAt: timestampSchema.nullable(),
    pendingCommands: z.number().int().nonnegative(),
    openAlarms: z.number().int().nonnegative().describe("Son 24 saatteki KRİTİK alarm sayısı"),
});
export const agentsSummarySchema = z.object({
    companyId: uuidSchema,
    generatedAt: timestampSchema,
    stations: z.array(agentStationSummarySchema),
});
export const agentsSummaryQuerySchema = z.object({ companyId: uuidSchema });
//# sourceMappingURL=admin.js.map